import React, { memo, useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import * as dagre from '@dagrejs/dagre';
import type { GraphBlock } from '../../../types/backend';
import BlockShell from './BlockShell';
import { chartPalette } from './blockFormat';

interface Props {
  block: GraphBlock;
}

const NODE_W = 140;
const NODE_H = 40;

function truncate(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`;
}

// dagre 3 makes Graph generic in its label types and defaults all three to `any`.
// Naming them keeps `g.graph()`, `g.node()` and `g.edge()` typed, so edge points
// stay Point[] instead of degrading to any under noImplicitAny.
type LayoutGraph = dagre.Graph<dagre.GraphLabel, dagre.NodeLabel, dagre.EdgeLabel>;

function buildLayout(block: GraphBlock): LayoutGraph {
  const g: LayoutGraph = new dagre.graphlib.Graph<
    dagre.GraphLabel,
    dagre.NodeLabel,
    dagre.EdgeLabel
  >({ multigraph: true });
  g.setGraph({ rankdir: 'TB', nodesep: 28, ranksep: 44, marginx: 8, marginy: 8 });
  g.setDefaultEdgeLabel(() => ({}));
  block.nodes.forEach((n) => g.setNode(n.id, { width: NODE_W, height: NODE_H, label: n.label }));
  block.edges.forEach((e, i) => g.setEdge(e.source, e.target, { label: e.label ?? '' }, `e${i}`));
  dagre.layout(g);
  return g;
}

function describe(block: GraphBlock): string {
  const labels = new Map(block.nodes.map((n) => [n.id, n.label]));
  const nodeList = block.nodes.map((n) => n.label).join(', ');
  const edges = block.edges
    .map((e) => {
      const rel = e.label ? ` (${e.label})` : '';
      return `${labels.get(e.source) ?? e.source} → ${labels.get(e.target) ?? e.target}${rel}`;
    })
    .join('; ');
  const noun = block.nodes.length === 1 ? 'node' : 'nodes';
  return `Graph with ${block.nodes.length} ${noun}: ${nodeList}.${edges ? ` Edges: ${edges}.` : ''}`;
}

const GraphBlockView: React.FC<Props> = memo(({ block }) => {
  const theme = useTheme();
  const colors = chartPalette(theme);
  const summary = useMemo(() => describe(block), [block]);
  const g = useMemo(() => buildLayout(block), [block]);
  const { width = NODE_W, height = NODE_H } = g.graph();
  const svgW = Math.ceil(width);
  const svgH = Math.ceil(height);
  const edgeStroke = theme.palette.text.secondary;

  // Colour by `group` when supplied (related nodes share a hue); otherwise fall
  // back to per-node index. Keeps the schema's optional grouping key meaningful.
  const groups = useMemo(
    () => [...new Set(block.nodes.map((n) => n.group).filter((gr): gr is string => Boolean(gr)))],
    [block],
  );
  const nodeColor = (node: (typeof block.nodes)[number], index: number): string =>
    node.group ? colors[groups.indexOf(node.group) % colors.length] : colors[index % colors.length];

  return (
    <BlockShell bodyPadding={1}>
      <Box role="img" aria-label={summary} sx={{ overflowX: 'auto' }}>
        <Box
          component="svg"
          viewBox={`0 0 ${svgW} ${svgH}`}
          preserveAspectRatio="xMidYMid meet"
          // Render at the diagram's intrinsic size, capped to the container and
          // centered — a narrow (single-column) DAG must not be upscaled to fill
          // the panel. Sizing lives entirely in sx (width + maxWidth cap + auto
          // height from the viewBox ratio) with no width/height DOM attributes,
          // so nothing fights the cap.
          sx={{
            display: 'block',
            mx: 'auto',
            // Explicit px: a bare number <= 1 is treated as a percentage by MUI's
            // sizing transform, which would silently upscale a degenerate layout.
            width: `${svgW}px`,
            maxWidth: '100%',
            height: 'auto',
            fontFamily: theme.typography.fontFamily,
          }}
        >
          <defs>
            <marker id="graph-arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
              <path d="M0,0 L8,4 L0,8 z" fill={edgeStroke} />
            </marker>
          </defs>
          {block.edges.map((e, i) => {
            const edge = g.edge(e.source, e.target, `e${i}`);
            if (!edge?.points?.length) return null;
            const points = edge.points.map((p) => `${p.x},${p.y}`).join(' ');
            const mid = edge.points[Math.floor(edge.points.length / 2)];
            return (
              <g key={i}>
                <polyline
                  points={points}
                  fill="none"
                  stroke={edgeStroke}
                  strokeWidth={1.5}
                  markerEnd="url(#graph-arrow)"
                />
                {e.label && mid && (
                  <text
                    x={mid.x}
                    y={mid.y - 4}
                    textAnchor="middle"
                    fontSize={10}
                    fill={theme.palette.text.secondary}
                  >
                    {truncate(e.label, 20)}
                  </text>
                )}
              </g>
            );
          })}
          {block.nodes.map((n, i) => {
            const node = g.node(n.id);
            // dagre 3 types x/y as optional: layout() fills them in, but a node it
            // could not place has none. Skip it, as the edge branch above already
            // skips an edge with no points, rather than inventing a position.
            if (node?.x === undefined || node.y === undefined) return null;
            const color = nodeColor(n, i);
            return (
              <g key={n.id}>
                <rect
                  x={node.x - NODE_W / 2}
                  y={node.y - NODE_H / 2}
                  width={NODE_W}
                  height={NODE_H}
                  rx={6}
                  fill={theme.palette.background.paper}
                  stroke={color}
                  strokeWidth={1.5}
                />
                <text
                  x={node.x}
                  y={node.y + 4}
                  textAnchor="middle"
                  fontSize={12}
                  fontWeight={600}
                  fill={theme.palette.text.primary}
                >
                  {truncate(n.label, 18)}
                </text>
              </g>
            );
          })}
        </Box>
        <Box component="span" sx={visuallyHidden}>
          {summary}
        </Box>
      </Box>
      {block.caption && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', px: 1.5, pb: 1, textAlign: 'center' }}
        >
          {block.caption}
        </Typography>
      )}
    </BlockShell>
  );
});

GraphBlockView.displayName = 'GraphBlockView';
export default GraphBlockView;
